"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NewProjectOwnerType = void 0;
exports.getValueStringForNewProjectOwner = getValueStringForNewProjectOwner;
exports.getNewProjectOwnerFromValueString = getNewProjectOwnerFromValueString;
exports.getNewProjectOwnerFromOrganizationMember = getNewProjectOwnerFromOrganizationMember;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * Before a project is created, it must be assigned an owner, this process is fairly complicated
 * because it can be changed at the last second by the Paywall (by the user, on the frontend), and the intended future
 * owner must be communicated to some backend functions (e.g. for fork/clone)
 *
 * This allows expressing the intended type of owner of a project being created.
 */
exports.NewProjectOwnerType = zod_1.z.enum(["current-user", "organization", "enterprise"]);
/**
 * In contexts where the user chooses the new project owner, they're often doing that with a MUI <Select>,
 * and the onChange handler for that component may only accept a string. So, we need to distinguish between
 * the above cases via a string, which can be given to a MenuItem's value prop.
 *
 * This function returns such a string, formed by concatenating parts of the new project owner type
 * with `:` (can't use -, nor _, because they're part of the identifiers)
 */
function getValueStringForNewProjectOwner(owner) {
    return owner.type === "current-user"
        ? "current-user"
        : owner.type === "organization"
            ? `organization:${owner.organizationUid}`
            : `enterprise:${owner.organizationUid}:${owner.enterpriseUid}`;
}
/**
 * The other half of a couple with getValueStringForNewProjectOwner(), this function gets an appropriate
 * NewProjectOwner instance for a given string value.
 *
 * Throws an error if the value can't be determined
 */
function getNewProjectOwnerFromValueString(value) {
    if (!value || typeof value !== "string") {
        throw new Error(`Failed to set selected option for new project owner: maybe nothing selected?`);
    }
    const parts = value.split(":");
    const ownerType = exports.NewProjectOwnerType.safeParse(parts[0]);
    const organizationUid = identifier_1.OrganizationUid.safeParse(parts[1]);
    const enterpriseUid = identifier_1.EnterpriseUid.safeParse(parts[2]);
    if (!ownerType.success) {
        throw new Error(`Invalid owner type on selected option for new project owner: ${ownerType.error}`);
    }
    if (ownerType.data === "current-user") {
        return { type: "current-user" };
    }
    if (!organizationUid.success) {
        throw new Error(`Invalid organization UID on selected option for new project owner: ${organizationUid.error}`);
    }
    if (ownerType.data === "organization") {
        return { type: "organization", organizationUid: organizationUid.data };
    }
    if (ownerType.data === "enterprise") {
        if (!enterpriseUid.success) {
            throw new Error(`Invalid enterprise UID on selected option for new project owner: ${enterpriseUid.error}`);
        }
        return { type: "enterprise", organizationUid: organizationUid.data, enterpriseUid: enterpriseUid.data };
    }
    // Above should be exhaustive already
    throw new Error(`Unhandled owner type: ${ownerType.data}`);
}
function getNewProjectOwnerFromOrganizationMember(member) {
    if (member.enterpriseUid) {
        return { type: "enterprise", organizationUid: member.organizationUid, enterpriseUid: member.enterpriseUid };
    }
    return { type: "organization", organizationUid: member.organizationUid };
}
//# sourceMappingURL=newProject.js.map